import { yupResolver } from "@hookform/resolvers/yup";
import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import {
  useGetInvoiceHistoryByProgramId,
  useReverseInvoiceMutation,
} from "../../../../api/services/client/clientApis";
import {
  MuiBox,
  MuiModal,
  MuiTypography,
  WarningConfirmationModal,
} from "../../../../components";
import { ManageRevenueSplit } from "../../../../components/manageRevenueSplit/ManageRevenueSplit";
import { STATUS_BADGE_TYPE } from "../../../../components/statusBadge/StatusBadge";
import { COMPONENT_IDS } from "../../../../helpers/constants/componentIds";
import {
  getAmountWithCurrency,
  getValueOrBlank,
  getValueOrZero,
  replaceHashWithValue,
} from "../../../../helpers/utils";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import {
  toggleLoader,
  toggleToast,
} from "../../../../redux/slices/appState/appStateSlice";
import { AmountBadgeText } from "../../../../styles/common.style";
import { useExportInvoiceHistoryToExcel } from "../../hooks/useExportInvoiceHistoryToExcel";
import {
  InvoiceReversalFormKey,
  ProgramInvoice,
  ProgramInvoiceItem,
} from "../../modelsAndSchemas/Models";
import { invoiceHistorySiteCoreKeys } from "../../siteCore/SiteCoreKeys";
import { invoiceReverseValidationSchema } from "../createProgramTabs/tabs/feesAndExpense/components/raiseInvoice/modelsAndSchema/ValidationSchemas";
import { InvoiceHistoryList } from "./components/invoiceHistoryList/InvoiceHistoryLists";
import InvoiceReversal from "./components/invoiceReversal/InvoiceReversal";
import { ViewInvoice } from "./components/viewInvoice/ViewInvoiceDetails";
import { InvoiceHistoryWrapper } from "./InvoiceHistory.style";
import { useDownloadInvoiceToExcel } from "../../hooks/useDownloadInvoiceToExcel";
import { DELIVERABLE_FRAMEWORK_TYPE } from "../../../../helpers/constants";

interface InvoiceHistoryProps {
  programId: string;
  isViewOnly?: boolean;
}

export const InvoiceHistory = ({
  programId,
  isViewOnly,
}: InvoiceHistoryProps) => {
  const { commonLabels, fetchLabel, commonValidationMessages, fetchValidationMessage } = useSiteCore();
  const selectedInvoiceRef = useRef<ProgramInvoice | null>(null);
  const getLabel = (key: keyof typeof invoiceHistorySiteCoreKeys) => {
    return fetchLabel(
      invoiceHistorySiteCoreKeys.invoiceHistoryTitle,
      invoiceHistorySiteCoreKeys[key],
    );
  };
  const [state, setState] = useState<{
    showReverseConfirmation: boolean;
    showViewInvoiceModal: boolean;
    showManageRevenueSplitModal: boolean;
    selectedInvoiceId: string;
    selectedInvoiceNumber: string;
    selectedInvoiceStatus: string;
    isInvoiceReversed?: boolean;
  }>({
    showReverseConfirmation: false,
    showViewInvoiceModal: false,
    showManageRevenueSplitModal: false,
    selectedInvoiceId: "",
    selectedInvoiceNumber: "",
    selectedInvoiceStatus: "",
    isInvoiceReversed: false,
  });

  const methods = useForm({
    mode: "all",
    defaultValues: {
      [InvoiceReversalFormKey.reversalDate]: "",
      [InvoiceReversalFormKey.reversalReason]: "",
      [InvoiceReversalFormKey.comments]: "",
    },
    resolver: yupResolver(
      invoiceReverseValidationSchema(fetchValidationMessage, commonValidationMessages),
    ),
  });

  const { handleSubmit, reset } = methods;

  const handleReversalSubmit = (data: Record<string, string | undefined>) => {
    reverseInvoice({
      invoiceId: selectedInvoiceRef.current?.id,
      date: dayjs().format(),
      reason: data[InvoiceReversalFormKey.reversalReason] ?? "",
      comments: data[InvoiceReversalFormKey.comments],
    });
    closeReverseConfirmation();
    reset();
  };

  const handleReverseAction = (selectedInvoice: ProgramInvoice) => {
    selectedInvoiceRef.current = selectedInvoice;
    setState((prevState) => ({ ...prevState, showReverseConfirmation: true }));
  };
  const closeReverseConfirmation = () => {
    reset();
    setState((prevState) => ({ ...prevState, showReverseConfirmation: false }));
  };
  const handleDelete = handleSubmit(handleReversalSubmit);
  const closeMainModalHandler = () => {
    setState((prevState) => ({ ...prevState, showViewInvoiceModal: false }));
  };

  const showViewInvoiceModal = (selectedInvoice: ProgramInvoice) => {
    setState((prevState) => ({
      ...prevState,
      showViewInvoiceModal: true,
      selectedInvoiceId: selectedInvoice.id,
      selectedInvoiceNumber: selectedInvoice.number,
      selectedInvoiceStatus: selectedInvoice.statusId,
    }));
  };

  const manageRevenueSplitClick = (selectedInvoice: ProgramInvoiceItem) => {
    setState((prevState) => ({
      ...prevState,
      showManageRevenueSplitModal: true,
      selectedInvoiceId: selectedInvoice.id,
      selectedInvoiceNumber: selectedInvoice.number,
      selectedInvoiceStatus: selectedInvoice.statusId,
      isInvoiceReversed: selectedInvoice.isInvoiceReversed,
    }));
  };

  const {
    data: invoiceHistory,
    refetchWithParams,
    isPending: isInvoiceHistoryFetching,
  } = useGetInvoiceHistoryByProgramId();

  useEffect(() => {
    refetchWithParams(programId);
  }, [programId]);

  const {
    mutate: reverseInvoice,
    isPending: isReversing,
    isSuccess: isReversed,
  } = useReverseInvoiceMutation();

  const dispatch = useDispatch();
  const { downloadExcel } = useExportInvoiceHistoryToExcel();
  const { downloadInvoiceExcel } = useDownloadInvoiceToExcel();

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible: isReversing || isInvoiceHistoryFetching,
      }),
    );
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [isReversing, isInvoiceHistoryFetching]);

  useEffect(() => {
    if (isReversed) {
      dispatch(
        toggleToast({
          open: true,
          message: getLabel("invoiceReversed"),
        }),
      );
      refetchWithParams(programId);
    }
  }, [isReversed]);

  const handleExportInvoice = async (selectedInvoice: ProgramInvoice) => {
    downloadExcel(selectedInvoice?.id, "program");
  };

  const handleDownloadInvoice = async (selectedInvoice: ProgramInvoice) => {
    downloadInvoiceExcel(selectedInvoice.id,  DELIVERABLE_FRAMEWORK_TYPE.PROGRAM);
  };

  return (
    <InvoiceHistoryWrapper>
      <MuiBox className="total-invoice-amount-label">
        <MuiTypography variant="h3SemiBold">
          {getLabel("totalInvoicedAmountAsOfToday")}{" "}
        </MuiTypography>
        <AmountBadgeText variant="h3Bold">
          {getAmountWithCurrency(
            getValueOrZero(invoiceHistory?.totalAmount),
            getValueOrBlank(invoiceHistory?.currency?.name),
          )}
        </AmountBadgeText>
      </MuiBox>
      {invoiceHistory?.invoices && (
        <InvoiceHistoryList
          onReverseClick={handleReverseAction}
          onViewInvoiceClick={showViewInvoiceModal}
          onManageRevenueSplitClick={manageRevenueSplitClick}
          onExportInvoiceClick={handleExportInvoice}
          onDownloadInvoiceClick={handleDownloadInvoice}
          data={invoiceHistory?.invoices}
          isViewOnly={isViewOnly}
          currency={invoiceHistory?.currency}
        />
      )}

      {state.showReverseConfirmation && (
        <WarningConfirmationModal
          labelContent={
            <FormProvider {...methods}>
              <InvoiceReversal invoiceId={selectedInvoiceRef.current?.number} />
            </FormProvider>
          }
          modalTitle={commonLabels.confirmation}
          show={state.showReverseConfirmation}
          onClose={closeReverseConfirmation}
          onPrimaryButtonClick={handleDelete}
          primaryButtonTitle={commonLabels.yesProceed}
          secondaryButtonTitle={commonLabels.no}
        />
      )}
      {state.showViewInvoiceModal && (
        <MuiModal
          id={COMPONENT_IDS.VIEW_INVOICE_MODAL}
          open={state.showViewInvoiceModal}
          onClose={closeMainModalHandler}
          title={replaceHashWithValue(
            getLabel(
              state.selectedInvoiceStatus === STATUS_BADGE_TYPE.invoiced
                ? "viewInvoiceHeader"
                : "viewReversedInvoiceHeader",
            ),
            state.selectedInvoiceNumber,
          )}
          secondaryButtonTitle={commonLabels.back}
          onSecondaryButtonClick={closeMainModalHandler}
          hasLargeContent
        >
          {state.selectedInvoiceId ? (
            <ViewInvoice
              invoiceId={state.selectedInvoiceId}
              programId={programId}
            />
          ) : (
            <></>
          )}
        </MuiModal>
      )}

      {state.showManageRevenueSplitModal && (
        <ManageRevenueSplit
          isOpen={true}
          isViewOnly={isViewOnly}
          invoiceId={state.selectedInvoiceId}
          invoiceNumber={state.selectedInvoiceNumber}
          invoiceStatus={state.selectedInvoiceStatus}
          isInvoiceReversed={state.isInvoiceReversed}
          onClose={() =>
            setState((prev) => ({
              ...prev,
              showManageRevenueSplitModal: false,
            }))
          }
          isForProject={false}
        />
      )}
    </InvoiceHistoryWrapper>
  );
};
